import { useLocalStorage } from '@shared/auth/useLocalStorage';
import moment from 'moment';
import CryptoJS from 'crypto-js';
import Compressor from 'compressorjs';

export const TextTruncate = (str: any, num: number, dot: boolean) => {
    try {
        if (str && str.length > num) {
            if (dot) {
                return str.substring(0, num) + '...';
            } else {
                return str.substring(0, num);
            }
        } else {
            return str;
        }
    } catch (error) {}
};

export const DateFormatPicker = (date: any, format: string = 'DD-MMM-YYYY') => {
    try {
        return moment(date).format(format);
    } catch (error) {
        return 'Invalid Date';
    }
};

export const checkUrlString = (url: string) => {
    if (url) {
        if (url.charAt(0) === '/') {
            return url.slice(1);
        }
        return url;
    }
    return '';
};

/**
 *
 * @param subMenu List of all menus
 * @param permissions Pemissions
 * @returns Boolean
 */
export const isSubmenuInPermissions = (subMenu, permissions) => {
    if (!subMenu || !Array.isArray(subMenu)) {
        return false;
    }

    for (const item of subMenu) {
        if (permissions.includes(item.route)) {
            return true;
        }

        if (item.subMenu) {
            const isSubmenuFound = isSubmenuInPermissions(item.subMenu, permissions);
            if (isSubmenuFound) {
                return true;
            }
        }
    }

    return false;
};

/***
 * Action Permissions Validator
 */

export enum ActionValues {
    Add = 'add',
    Edit = 'edit',
    Delete = 'delete',
    View = 'view',
}
export const PERMISSIONS_ACTIONS_LIST = ['View', 'Add', 'Edit', 'Delete'];

export const actionsPermissionValidator = (modulePath: string, action: ActionValues) => {
    const user: string | null = localStorage.getItem('user');
    if (user) {
        const userParsed = JSON.parse(user);
        if (userParsed) {
            const userAllPermissions = userParsed.user_role.user_permissions;
            const routeMatchedPermissions = userAllPermissions.find(
                item => item.slug === modulePath.replace(/\/$/, ''),
            );
            let allowedActions = routeMatchedPermissions?.role_permissions?.actionIds || [];

            // Convert all to Lower Case
            allowedActions = allowedActions.map(item => item.toLowerCase());
            return allowedActions.includes(action);
        }
    }
    return false;
};

/**
 * Check if any of two permissios are there
 */

export const hasMinRequiredPermissions = userPermissions => {
    const intersection = userPermissions.filter(permission => PERMISSIONS_ACTIONS_LIST.includes(permission));
    return intersection.length >= 3;
};

/**
 * Exclude permission list
 */
export const excludePermissions = ['/home/manageRoles', '/home/dashboard', '/home/penalty', '/home/vendordelay'];

/**
 *
 * @param password
 * @returns
 */

export const encryptPassword = (password: any) => {
    const encryptedData = CryptoJS.AES.encrypt(password, process.env.REACT_APP_CRYPTO_SECRET_KEY).toString();

    return encryptedData;
};

export const capitalizeFirstLetter = (string: any) => {
    try {
        return string.charAt(0).toUpperCase() + string.slice(1);
    } catch (error) {
        return '';
    }
};

/**
 *
 */
export const DEMAND_DATE_TIME_HOURS = {
    HOUR24: 2 * 60 * 60 * 1000,
    HOUR48: 4 * 60 * 60 * 1000,
    HOUR72: 6 * 60 * 60 * 1000,
};

//This method used for calculate image Height and Width
const calculateImageHeightWidth = file => {
    return new Promise((resolve, reject) => {
        try {
            const reader = new FileReader();
            reader.onload = event => {
                const img: any = new Image();
                img.onload = () => {
                    const maxWidth: any = process.env.REACT_APP_MAX_WIDTH || 1024; //1280; // 1024
                    const maxHeight: any = process.env.REACT_APP_MAX_HEIGHT || 520; //720; //520
                    let width: any = img.width;
                    let height: any = img.height;

                    if (width > height) {
                        if (width > maxWidth) {
                            height = Math.round((height * maxWidth) / width);
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxHeight) {
                            width = Math.round((width * maxHeight) / height);
                            height = maxHeight;
                        }
                    }
                    resolve({ width, height });
                };
                img.onerror = function() {
                    reject(false);
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        } catch (error) {
            reject(false);
        }
    });
};

// This is method used to call compress image function
export const imageCompress = async file => {
    try {
        const prevsize = file.size;

        if (prevsize / 1024 < 130 || file.type == 'application/pdf') {
            const fileName = `${Date.now()}.${file.type.split('/').pop()}`;
            const renamedFile = new File([file], fileName, { type: file.type });
            // file.name = renamedFile;
            return renamedFile;
        } else {
            const imgHW = await calculateImageHeightWidth(file);
            const compressFile = await imageCompressFn(file, 0.7, prevsize, imgHW);
            return compressFile;
        }
    } catch (error) {
        return false;
    }
};

// This is method used to compress image till the size gets achived
const imageCompressFn = async (file, quality, prevsize, imgHW) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
        try {
            const compressedFile = await new Promise((resolve, reject) => {
                const compressor = new Compressor(file, {
                    quality: quality,
                    maxWidth: imgHW.width,
                    maxHeight: imgHW.height,
                    // // resize: "contain",
                    // // mimeType: "auto",
                    convertSize: 150,
                    success(result) {
                        if (result.size > 150 * 1024 && quality > 0.1 && prevsize != result.size) {
                            // Recursively compress with a lower quality
                            let qtly = quality > 0.2 ? quality - 0.1 : quality;
                            qtly = parseFloat(qtly.toFixed(1));
                            resolve(imageCompressFn(file, qtly, result.size, imgHW));
                            compressor.abort();
                        } else {
                            const fileName = `${Date.now()}.${result.type.split('/').pop()}`;
                            const renamedFile = new File([result], fileName, { type: result.type });
                            resolve(renamedFile);
                            compressor.abort();
                        }
                    },
                    error(err) {
                        compressor.abort();
                        reject(err.message);
                    },
                });
            });

            resolve(compressedFile);
        } catch (error) {
            reject(error);
        }
    });
};
